if (!customElements.get('wove-collection-facets')) {
  class WoveCollectionFacets extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.renderTimer = null;
      this.sectionCache = new Map();
      this.onClick = this.handleClick.bind(this);
      this.onChange = this.handleChange.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onPopState = this.handlePopState.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('popstate', this.onPopState);
      if (this.abortController) this.abortController.abort();
      window.clearTimeout(this.renderTimer);
    }

    handleClick(event) {
      const facetToggle = event.target.closest('[data-wove-facet-toggle]');
      if (facetToggle && this.contains(facetToggle)) {
        event.preventDefault();
        this.toggleDesktopPanel(facetToggle);
        return;
      }

      const sortToggle = event.target.closest('[data-wove-sort-toggle]');
      if (sortToggle && this.contains(sortToggle)) {
        event.preventDefault();
        this.toggleDesktopPanel(sortToggle);
        return;
      }

      const mobileOpen = event.target.closest('[data-wove-mobile-open]');
      if (mobileOpen && this.contains(mobileOpen)) {
        event.preventDefault();
        this.openMobileSheet(mobileOpen.dataset.woveMobileOpen, mobileOpen);
        return;
      }

      const mobileClose = event.target.closest('[data-wove-mobile-close]');
      if (mobileClose && this.contains(mobileClose)) {
        event.preventDefault();
        this.closeMobileSheets(true);
        return;
      }

      const reset = event.target.closest('[data-wove-reset]');
      if (reset && this.contains(reset)) {
        event.preventDefault();
        this.closeMobileSheets(false);
        this.render('', true, reset.id || null);
        return;
      }

      const ajaxLink = event.target.closest('[data-wove-ajax-link]');
      if (ajaxLink && this.contains(ajaxLink)) {
        event.preventDefault();
        const url = new URL(ajaxLink.href, window.location.origin);
        const scrollToGrid = ajaxLink.hasAttribute('data-wove-pagination');
        this.render(url.searchParams.toString(), true, ajaxLink.id || null, scrollToGrid);
      }
    }

    handleChange(event) {
      const desktopForm = event.target.closest('[data-wove-desktop-form]');
      if (desktopForm && this.contains(desktopForm)) {
        const focusId = event.target.id || null;
        window.clearTimeout(this.renderTimer);
        this.renderTimer = window.setTimeout(() => {
          this.render(this.paramsFromForm(desktopForm), true, focusId);
        }, 180);
        return;
      }

      const mobileSortForm = event.target.closest('[data-wove-mobile-sort-form]');
      if (mobileSortForm && this.contains(mobileSortForm)) {
        const params = new URLSearchParams(window.location.search);
        params.delete('page');
        params.set('sort_by', event.target.value);
        this.closeMobileSheets(false);
        this.render(params.toString(), true, null);
      }
    }

    handleSubmit(event) {
      const form = event.target.closest('[data-wove-mobile-filter-form], [data-wove-desktop-form]');
      if (!form || !this.contains(form)) return;
      event.preventDefault();
      this.closeMobileSheets(false);
      this.render(this.paramsFromForm(form), true, null);
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;
      this.closeDesktopPanels();
      this.closeMobileSheets(true);
    }

    handleDocumentClick(event) {
      if (!this.contains(event.target)) {
        this.closeDesktopPanels();
        return;
      }

      if (!event.target.closest('[data-wove-desktop-facet], [data-wove-sort-wrap]')) {
        this.closeDesktopPanels();
      }
    }

    handlePopState() {
      this.render(window.location.search.slice(1), false, null);
    }

    paramsFromForm(form) {
      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (String(value).trim() !== '') params.append(key, value);
      });
      params.delete('page');
      return params.toString();
    }

    toggleDesktopPanel(toggle) {
      const panel = this.querySelector(`#${CSS.escape(toggle.getAttribute('aria-controls'))}`);
      if (!panel) return;
      const shouldOpen = panel.hidden;
      this.closeDesktopPanels(toggle);
      panel.hidden = !shouldOpen;
      panel.classList.toggle('wove-facet-panel--open', shouldOpen);
      toggle.setAttribute('aria-expanded', String(shouldOpen));
    }

    closeDesktopPanels(exceptToggle = null) {
      this.querySelectorAll('[data-wove-facet-toggle], [data-wove-sort-toggle]').forEach((toggle) => {
        if (toggle === exceptToggle) return;
        const panel = this.querySelector(`#${CSS.escape(toggle.getAttribute('aria-controls'))}`);
        if (panel) {
          panel.hidden = true;
          panel.classList.remove('wove-facet-panel--open');
        }
        toggle.setAttribute('aria-expanded', 'false');
      });
    }

    openMobileSheet(name, trigger) {
      const sheet = this.querySelector(`[data-wove-sheet="${name}"]`);
      if (!sheet) return;
      this.mobileTrigger = trigger;
      sheet.hidden = false;
      sheet.classList.remove('shop-filter-bar__modalimage');
      sheet.classList.add('shop-filter-bar__modalimageopen');
      document.body.classList.add('overflow-hidden');
      window.requestAnimationFrame(() => sheet.querySelector('[data-wove-mobile-close]')?.focus());
    }

    closeMobileSheets(restoreFocus) {
      this.querySelectorAll('[data-wove-sheet]').forEach((sheet) => {
        sheet.classList.remove('shop-filter-bar__modalimageopen');
        sheet.classList.add('shop-filter-bar__modalimage');
        sheet.hidden = true;
      });
      document.body.classList.remove('overflow-hidden');
      if (restoreFocus && this.mobileTrigger?.isConnected) this.mobileTrigger.focus();
    }

    async render(searchParams, updateHistory, focusId, scrollToGrid = false) {
      window.clearTimeout(this.renderTimer);
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      const collectionPath = this.dataset.collectionUrl || window.location.pathname;
      const browserUrl = `${collectionPath}${searchParams ? `?${searchParams}` : ''}`;
      const requestUrl = new URL(browserUrl, window.location.origin);
      requestUrl.searchParams.set('section_id', this.dataset.sectionId);
      const cacheKey = requestUrl.toString();

      this.setLoading(true);

      try {
        let html = this.sectionCache.get(cacheKey);
        if (!html) {
          const response = await fetch(cacheKey, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
            signal: this.abortController.signal,
          });
          if (!response.ok) throw new Error(`Collection update failed with ${response.status}`);
          html = await response.text();
          this.sectionCache.set(cacheKey, html);
        }

        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const replacement = parsed.getElementById(this.id);
        if (!replacement) throw new Error('Collection section was missing from the AJAX response');

        this.closeMobileSheets(false);
        this.innerHTML = replacement.innerHTML;
        this.setLoading(false);

        if (updateHistory) history.pushState({ searchParams }, '', browserUrl);

        if (focusId) {
          window.requestAnimationFrame(() => this.querySelector(`#${CSS.escape(focusId)}`)?.focus());
        }

        if (scrollToGrid) {
          window.requestAnimationFrame(() => {
            this.querySelector('[data-wove-grid-region]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        window.location.assign(browserUrl);
      }
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      this.setAttribute('aria-busy', String(isLoading));
      const status = this.querySelector('[data-wove-live-status]');
      if (status) status.textContent = isLoading ? 'Updating products…' : '';
    }
  }

  customElements.define('wove-collection-facets', WoveCollectionFacets);
}
